import type { IconComponent } from '@digital-twins/design-system/icons';
import { STUDY_GOAL_ICONS } from '@/lib/icons/studyGoalIcons';
import type { GoalCategoryId, GoalId } from "./types";
import { goalCardStrings } from "./strings";

/**
 * Goal config — data, not hardcoded blocks. All 8 goals ship in the UI at
 * once; backend wiring lands in waves and the UI doesn't know or care.
 *
 * Illustrations: every goal has bespoke artwork (goal-art-*.webp) — the
 * conceptual-pun suite: one bold object + one visual pun per decision on a
 * warm cream field, slate-ink shapes, risograph grain, single category
 * accent (emerald/sky/amber), motif staged in the lower 65% so it survives
 * the bottom-anchored object-cover crop at every bento footprint. See
 * docs/plans/goal-card-illustration-suite.md.
 */

export interface GoalIllustration {
  light: string;
  dark: string;
  /**
   * New art series (conceptual-pun suite —
   * docs/plans/goal-card-illustration-suite.md) renders as a full-bleed
   * painted band (object-cover + top fade) instead of a floating vignette.
   */
  fullBleed?: boolean;
  /** Optional per-art positioning for full-bleed assets with unusual crop needs. */
  fullBleedClassName?: string;
}

export interface GoalDefinition {
  id: GoalId;
  /** Presentational grouping for the goal screen filter. */
  category: GoalCategoryId;
  title: string;
  subtitle: string;
  /** Classic MRX study name shown as the card tag (e.g. "Ad Testing"). */
  methodTag: string;
  /** Lucide glyph that hints the study type in the flow-header GoalChip. */
  icon: IconComponent;
  illustration: GoalIllustration;
  /**
   * When true the card renders with a brand-tinted background (primary/20)
   * to visually distinguish it as the recommended / featured study type.
   */
  featured?: boolean;
  /**
   * Goal only ships as a full study today; the Quick Study arc for it is not
   * ready. Quick mode renders the card locked with "Coming soon".
   */
  fullStudyOnly?: boolean;
}

/**
 * "One bold object, one visual pun per decision" suite — Comp B direction
 * from docs/plans/goal-card-illustration-suite.md. Day art doubles as the
 * dark variant until the dark batch lands.
 */
const ART_ILLUSTRATIONS = {
  conceptTesting: {
    light: "/illustrations/goal-art-concept-validation.webp",
    dark: "/illustrations/goal-art-concept-validation.webp",
    fullBleed: true,
  },
  adMessaging: {
    light: "/illustrations/goal-art-ad-creative.webp",
    dark: "/illustrations/goal-art-ad-creative.webp",
    fullBleed: true,
    fullBleedClassName: "object-[center_74%]",
  },
  brandPerception: {
    light: "/illustrations/goal-art-brand-perception.webp",
    dark: "/illustrations/goal-art-brand-perception.webp",
    fullBleed: true,
  },
  customerDiscovery: {
    light: "/illustrations/goal-art-explore.webp",
    dark: "/illustrations/goal-art-explore.webp",
    fullBleed: true,
  },
  landingPage: {
    light: "/illustrations/goal-art-lp-conversion.webp",
    dark: "/illustrations/goal-art-lp-conversion.webp",
    fullBleed: true,
  },
  messageTesting: {
    light: "/illustrations/goal-art-claim-validation.webp",
    dark: "/illustrations/goal-art-claim-validation.webp",
    fullBleed: true,
  },
  needsAssessment: {
    light: "/illustrations/goal-art-pain-ranking.webp",
    dark: "/illustrations/goal-art-pain-ranking.webp",
    fullBleed: true,
  },
  pricing: {
    light: "/illustrations/goal-art-pricing-packaging.webp",
    dark: "/illustrations/goal-art-pricing-packaging.webp",
    fullBleed: true,
  },
} as const;

/**
 * explore leads as the featured / recommended card. The remaining goals follow
 * the original grouping: test (4) · brand (2) · explore (1, pain_ranking).
 */
export const GOALS: Array<GoalDefinition> = [
  {
    id: "explore",
    category: "explore",
    icon: STUDY_GOAL_ICONS.explore,
    featured: true,
    ...goalCardStrings.explore,
    illustration: ART_ILLUSTRATIONS.customerDiscovery,
  },
  {
    id: "lp_conversion",
    category: "test",
    icon: STUDY_GOAL_ICONS.lp_conversion,
    ...goalCardStrings.lp_conversion,
    illustration: ART_ILLUSTRATIONS.landingPage,
  },
  {
    id: "ad_creative",
    category: "test",
    icon: STUDY_GOAL_ICONS.ad_creative,
    ...goalCardStrings.ad_creative,
    illustration: ART_ILLUSTRATIONS.adMessaging,
  },
  {
    id: "concept_validation",
    category: "test",
    icon: STUDY_GOAL_ICONS.concept_validation,
    ...goalCardStrings.concept_validation,
    illustration: ART_ILLUSTRATIONS.conceptTesting,
  },
  {
    id: "pricing_packaging",
    category: "test",
    icon: STUDY_GOAL_ICONS.pricing_packaging,
    ...goalCardStrings.pricing_packaging,
    illustration: ART_ILLUSTRATIONS.pricing,
    fullStudyOnly: true,
  },
  {
    id: "brand_perception",
    category: "brand",
    icon: STUDY_GOAL_ICONS.brand_perception,
    ...goalCardStrings.brand_perception,
    illustration: ART_ILLUSTRATIONS.brandPerception,
    fullStudyOnly: true,
  },
  {
    id: "claim_validation",
    category: "brand",
    icon: STUDY_GOAL_ICONS.claim_validation,
    ...goalCardStrings.claim_validation,
    illustration: ART_ILLUSTRATIONS.messageTesting,
  },
  {
    id: "pain_ranking",
    // Pains are seeded as concept assets (contract §0).
    category: "explore",
    icon: STUDY_GOAL_ICONS.pain_ranking,
    ...goalCardStrings.pain_ranking,
    illustration: ART_ILLUSTRATIONS.needsAssessment,
  },
];

export function getGoalById(goalId: string | null): GoalDefinition | null {
  if (!goalId) return null;
  return GOALS.find((goal) => goal.id === goalId) ?? null;
}
